import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Lightbulb, Ruler, Wrench } from "lucide-react";
import { products } from "@/data/products";
import { useLeadForm } from "@/components/lead/LeadForm";
import { SEO, breadcrumbLd, faqLd } from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { ProductCard } from "@/components/site/ProductCard";
import { FAQ } from "@/components/site/FAQ";
import { CTASection } from "@/components/site/CTASection";

export default function ProductPage({ slugOverride }) {
  const { slug: routeSlug } = useParams();
  const slug = slugOverride || routeSlug;
  const { openForm } = useLeadForm();
  const product = products.find((p) => p.slug === slug);
  if (!product) return <Navigate to="/blinds" replace />;
  const related = products.filter((p) => product.related.includes(p.slug));

  return (
    <>
      <SEO
        title={`${product.name} Birmingham | Made to Measure & Fitted | My Blinds Company`}
        description={`${product.short} Professionally measured and fitted across Birmingham by My Blinds Company. Book a free home design visit.`}
        path={product.slug === "shutters" ? "/shutters" : `/${product.slug}`}
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Blinds", path: "/blinds" },
            { name: product.name, path: product.slug === "shutters" ? "/shutters" : `/${product.slug}` },
          ]),
          faqLd(product.faqs),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: `Made-to-measure ${product.name}`,
            provider: { "@type": "LocalBusiness", name: "My Blinds Company Ltd" },
            areaServed: "Birmingham, UK",
          },
        ]}
      />

      {/* HERO */}
      <section className="relative bg-navy-950 text-white overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-white/50 mb-4">
              <Link to="/blinds" className="hover:text-white transition-colors">Blinds</Link>
              <span className="mx-2">/</span>
              <span className="text-white/80">{product.name}</span>
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight">
              {product.name}, Made to Measure
            </h1>
            <p className="mt-3 font-serif italic text-xl text-white/70">
              {product.heroTagline}
            </p>
            <p className="mt-5 text-base leading-relaxed text-white/70">
              {product.short}
            </p>
            <button
              type="button"
              data-testid="product-book-visit-btn"
              onClick={() => openForm({ source: `product_${product.slug}`, productInterest: "Blinds" })}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-navy-900 shadow-xl transition-all duration-300 hover:-translate-y-0.5"
            >
              Book a Free Design Visit <ArrowRight className="h-4 w-4" />
            </button>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-[2rem] aspect-[4/3] shadow-2xl">
              <img
                src={product.image}
                alt={`Made-to-measure ${product.name.toLowerCase()} professionally fitted`}
                className="h-full w-full object-cover"
                fetchpriority="high"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* IN ACTION — optional product video */}
      {product.video && (
        <section className="bg-navy-950 pb-16 sm:pb-24" data-testid="product-video-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="overflow-hidden rounded-[2rem] shadow-2xl border border-white/10 aspect-video bg-navy-900">
                <video
                  data-testid="product-video"
                  className="h-full w-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  onCanPlay={(e) => e.currentTarget.play().catch(() => {})}
                  poster={product.videoPoster}
                >
                  <source src={product.video} type="video/mp4" />
                </video>
              </div>
              <p className="text-center text-xs text-white/50 mt-4">
                See them in action — smooth, quiet and completely cord-free.
              </p>
            </Reveal>
          </div>
        </section>
      )}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <Eyebrow>About {product.name}</Eyebrow>
            {product.intro.map((p, i) => (
              <p key={i} className="mt-5 text-base sm:text-lg leading-relaxed text-slate-600">
                {p}
              </p>
            ))}
            <div className="mt-8 rounded-2xl border border-warm-200 bg-warm-100/60 p-6 flex items-start gap-4">
              <Lightbulb className="h-5 w-5 mt-0.5 shrink-0 text-navy-800" />
              <div>
                <h3 className="font-sans text-base font-semibold text-slate-900 mb-1">Light & privacy</h3>
                <p className="text-sm leading-relaxed text-slate-600">{product.light}</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="rounded-2xl border border-warm-200 bg-white p-7 shadow-sm">
              <h2 className="font-serif text-2xl text-slate-900 mb-5">Why you'll love them</h2>
              <ul className="space-y-3.5">
                {product.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-slate-700">
                    <CheckCircle2 className="h-5 w-5 mt-0 text-navy-800 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <h3 className="font-sans text-sm font-semibold uppercase tracking-[0.15em] text-slate-500 mt-8 mb-3">
                Ideal for
              </h3>
              <div className="flex flex-wrap gap-2">
                {product.rooms.map((r) => (
                  <span key={r} className="rounded-full bg-warm-100 border border-warm-200 px-3.5 py-1.5 text-xs font-medium text-slate-700">
                    {r}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OPTIONS */}
      <section className="py-16 sm:py-24 bg-warm-100/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-10">
            <Eyebrow>Styles & Finishes</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Choices you'll make at your home visit
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
            {product.options.map((o, i) => (
              <Reveal key={o} delay={i * 0.05}>
                <div className="flex items-start gap-3 rounded-xl border border-warm-200 bg-white p-5">
                  <CheckCircle2 className="h-5 w-5 mt-0 shrink-0 text-navy-800" />
                  <p className="text-sm text-slate-700">{o}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MEASURING & FITTING */}
      <section className="py-16 sm:py-24 bg-navy-950 grain text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-2 gap-8">
          <Reveal>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8 h-full">
              <Ruler className="h-6 w-6 text-white/70 mb-4" />
              <h2 className="font-serif text-2xl mb-3">Measured for you</h2>
              <p className="text-sm leading-relaxed text-white/70">
                Every {product.name.toLowerCase().replace(/ blinds$/, "")} blind
                we fit starts with professional measuring at your free home
                visit. We account for recess depth, handles, tiles and
                anything else that affects the fit — so your blind is made for
                your window, not approximately for it.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8 h-full">
              <Wrench className="h-6 w-6 text-white/70 mb-4" />
              <h2 className="font-serif text-2xl mb-3">Fitted and checked</h2>
              <p className="text-sm leading-relaxed text-white/70">
                Our experienced fitters install your blinds, check every
                control operates smoothly, fit child-safety devices where
                needed and take the packaging away. All you do is enjoy the
                result.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQS */}
      <section className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-8">
            <Eyebrow>Questions</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl text-slate-900 tracking-tight">
              {product.name} — FAQs
            </h2>
          </Reveal>
          <FAQ items={product.faqs} idPrefix={`${product.slug}-faq`} />
        </div>
      </section>

      {/* RELATED */}
      <section className="pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-10">
            <h2 className="font-serif text-3xl text-slate-900 tracking-tight">
              You might also consider
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <ProductCard
                  className="h-full"
                  product={{ slug: p.slug, name: p.name, short: p.short, image: p.image, href: `/blinds/${p.slug}` }}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title={`Ready to see ${product.name.toLowerCase()} in your home?`}
        text="Book a free design visit — we'll bring samples, measure up and leave you with a clear, no-obligation quote."
        source={`product_${product.slug}_cta`}
      />
    </>
  );
}
